import { useDebounce } from '@doneverse/data/hooks'
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { FormControl } from '@/components/ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export type ComboboxOption = {
  value: string
  label: string
  description?: string
}

export type ComboboxGroup = {
  heading?: string
  options: ComboboxOption[]
}

type CommonProps = {
  /** Currently selected value. */
  value: string
  /** Called with the new value when the selection changes. */
  onValueChange: (value: string) => void
  disabled?: boolean
  /** Trigger text when nothing is selected. */
  placeholder?: string
  /** Search input placeholder. */
  searchPlaceholder?: string
  /** Text shown when nothing matches. */
  emptyText?: string
  /** Accessible label for the trigger button. */
  'aria-label'?: string
  /** Accessible label for the search input. */
  searchAriaLabel?: string
  /** Wrap the trigger in FormControl for react-hook-form integration. */
  asFormControl?: boolean
  /** Open the popover on mount (e.g. when revealed by a parent toggle). */
  defaultOpen?: boolean
  /** Notified whenever the popover opens or closes (select, dismiss, etc.). */
  onOpenChange?: (open: boolean) => void
}

type StaticComboboxProps = CommonProps & {
  /** Static list filtered client-side by cmdk. Default mode. */
  mode?: 'static'
  options: ComboboxOption[]
}

type AsyncComboboxProps = CommonProps & {
  /** Server-driven search: the parent owns the query and filtering. */
  mode: 'async'
  /** Whether the query is currently fetching. */
  isFetching: boolean
  /** Options to display, already filtered by the parent. */
  groups: ComboboxGroup[]
  /** Called with the debounced query when open, or null when closed. */
  onSearchChange: (search: string | null) => void
  /** Debounce delay in ms for the search input. */
  debounceMs?: number
  /** Text to show while loading and no options exist yet. */
  loadingText?: string
  /** Options pinned to the top regardless of search (e.g. "Unassigned"). */
  pinnedOptions?: ComboboxOption[]
}

export type ComboboxProps = StaticComboboxProps | AsyncComboboxProps

function OptionContent({ option }: { option: ComboboxOption }) {
  if (!option.description) return <span>{option.label}</span>
  return (
    <span className="flex flex-col">
      <span>{option.label}</span>
      <span className="text-xs text-muted-foreground">{option.description}</span>
    </span>
  )
}

/**
 * Searchable single-select built on Popover + cmdk.
 *
 * - `mode="static"` (default): pass `options`; cmdk filters them client-side.
 *   Use for fixed lists (e.g. timezones).
 * - `mode="async"`: pass `groups` + `isFetching` + `onSearchChange`; the parent
 *   owns the query and filtering (cmdk's own filter is disabled). Use for
 *   server-backed search (e.g. the admin user pickers).
 */
export const Combobox = forwardRef<HTMLButtonElement, ComboboxProps>(function Combobox(props, ref) {
  const {
    value,
    onValueChange,
    disabled = false,
    placeholder = 'Select...',
    searchPlaceholder = 'Search...',
    emptyText = 'No results found.',
    'aria-label': ariaLabel,
    searchAriaLabel,
    asFormControl = false,
    defaultOpen = false,
    onOpenChange,
  } = props

  const isAsync = props.mode === 'async'
  const groups: ComboboxGroup[] = isAsync ? props.groups : [{ options: props.options }]
  const pinnedOptions = isAsync ? props.pinnedOptions : undefined
  const isFetching = isAsync ? props.isFetching : false
  const onSearchChange = isAsync ? props.onSearchChange : undefined
  const debounceMs = isAsync ? (props.debounceMs ?? 300) : 0
  const loadingText = isAsync ? (props.loadingText ?? 'Loading...') : 'Loading...'

  const [open, setOpen] = useState(defaultOpen)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, debounceMs)
  // Remember the label of the last user-picked option so it survives query key changes.
  const pickedRef = useRef<{ value: string; label: string } | null>(null)

  // Async mode: notify the parent of the debounced query while open.
  useEffect(() => {
    if (open && onSearchChange) onSearchChange(debouncedSearch)
  }, [open, debouncedSearch, onSearchChange])

  useEffect(() => {
    if (!open) {
      setSearch('')
      onSearchChange?.(null)
    }
  }, [open, onSearchChange])

  const allOptions = groups.flatMap((g) => g.options)
  const hasOptions = allOptions.length > 0
  // Loading / empty are only managed manually in async mode (cmdk's filter is
  // off). Static mode lets cmdk drive the empty state via CommandEmpty.
  const showLoading = isAsync && isFetching && !hasOptions
  const showAsyncEmpty = isAsync && !isFetching && !hasOptions

  // Resolve the display label for the current value.
  const resolvedLabel = (() => {
    const pinned = pinnedOptions?.find((o) => o.value === value)
    if (pinned) return pinned.label
    const fromOptions = allOptions.find((o) => o.value === value)
    if (fromOptions) return fromOptions.label
    if (pickedRef.current?.value === value) return pickedRef.current.label
    return null
  })()

  function handleOpenChange(next: boolean) {
    setOpen(next)
    onOpenChange?.(next)
  }

  function handleSelect(option: ComboboxOption) {
    pickedRef.current = option
    onValueChange(option.value)
    handleOpenChange(false)
  }

  const trigger = (
    <Button
      ref={ref}
      type="button"
      variant="outline"
      role="combobox"
      aria-expanded={open}
      aria-label={ariaLabel}
      disabled={disabled}
      className="w-full justify-between font-normal"
    >
      <span className={cn('truncate text-left', !resolvedLabel && 'text-muted-foreground')}>
        {resolvedLabel || placeholder}
      </span>
      {isFetching ? (
        <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin" />
      ) : (
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      )}
    </Button>
  )

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {asFormControl ? <FormControl>{trigger}</FormControl> : trigger}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
        {/* Static mode: let cmdk filter. Async mode: parent already filtered. */}
        <Command shouldFilter={!isAsync} label={searchAriaLabel}>
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder={searchPlaceholder}
            aria-label={searchAriaLabel}
          />
          <CommandList>
            {!isAsync && <CommandEmpty>{emptyText}</CommandEmpty>}
            {pinnedOptions && pinnedOptions.length > 0 && (
              <CommandGroup>
                {pinnedOptions.map((option) => (
                  <CommandItem key={option.value} value={option.value} onSelect={() => handleSelect(option)}>
                    <Check
                      className={cn('mr-2 h-4 w-4', value === option.value ? 'opacity-100' : 'opacity-0')}
                    />
                    <OptionContent option={option} />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {showLoading && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {loadingText}
              </div>
            )}
            {showAsyncEmpty && (
              <div className="py-6 text-center text-sm text-muted-foreground">{emptyText}</div>
            )}
            {groups.map((group, i) =>
              group.options.length > 0 ? (
                <CommandGroup key={group.heading ?? i} heading={group.heading}>
                  {group.options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={`${option.value}-${option.label}`}
                      onSelect={() => handleSelect(option)}
                    >
                      <Check
                        className={cn('mr-2 h-4 w-4', value === option.value ? 'opacity-100' : 'opacity-0')}
                      />
                      <OptionContent option={option} />
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null,
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
})
